import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CurrencyInr, ArrowsClockwise, Receipt, ShoppingCart, Money, CreditCard, Bank as BankIcon, DeviceMobile, Notebook, Download } from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const METHOD_META = {
  Cash: { icon: Money, color: "text-emerald-600", bg: "bg-emerald-50", ring: "border-emerald-200", emoji: "💵" },
  UPI: { icon: DeviceMobile, color: "text-indigo-600", bg: "bg-indigo-50", ring: "border-indigo-200", emoji: "📱" },
  Card: { icon: CreditCard, color: "text-amber-600", bg: "bg-amber-50", ring: "border-amber-200", emoji: "💳" },
  "Bank Transfer": { icon: BankIcon, color: "text-cyan-600", bg: "bg-cyan-50", ring: "border-cyan-200", emoji: "🏦" },
  Cheque: { icon: Notebook, color: "text-slate-600", bg: "bg-slate-50", ring: "border-slate-200", emoji: "📄" },
};

export default function Collections() {
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillRows, setDrillRows] = useState([]);
  const [period, setPeriod] = useState("today");

  const load = useCallback(() => api.get("/billing/collection-dashboard").then(r => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openDrill = async (p, method) => {
    setDrill({ period: p, method });
    try {
      const { data } = await api.get("/billing/collection-detail", { params: { period: p, method } });
      setDrillRows(data);
    } catch { setDrillRows([]); }
  };

  const current = data?.[period] || null;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-semibold mb-2 flex items-center gap-2">
            <CurrencyInr size={14} weight="fill" /> End-of-Day Reconciliation
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Billing Collection Dashboard</h1>
          <p className="text-sm text-slate-600 mt-1.5">Application bills + POS bills, split by payment method. Click any number to see individual transactions.</p>
        </div>
        <Button variant="outline" onClick={load} className="gap-2" data-testid="col-refresh"><ArrowsClockwise size={14} /> Refresh</Button>
      </div>

      <Tabs value={period} onValueChange={setPeriod}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="today" data-testid="col-tab-today">Today</TabsTrigger>
          <TabsTrigger value="month" data-testid="col-tab-month">This Month</TabsTrigger>
          <TabsTrigger value="total" data-testid="col-tab-total">All-time</TabsTrigger>
        </TabsList>
      </Tabs>

      {current && (
        <>
          {/* Payment method grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {current.by_method.map((m) => {
              const meta = METHOD_META[m.method] || METHOD_META.Cash;
              const Icon = meta.icon;
              return (
                <Card
                  key={m.method}
                  data-testid={`col-method-${m.method.replace(/\s+/g, "-")}`}
                  className={`p-3 cursor-pointer hover:shadow-md transition-shadow border-l-4 ${meta.bg}`}
                  style={{ borderLeftColor: `var(--${meta.color.replace("text-", "")}-500, #94a3b8)` }}
                  onClick={() => openDrill(period, m.method)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${meta.color}`}>{meta.emoji} {m.method}</div>
                    <Icon size={14} weight="fill" className={meta.color} />
                  </div>
                  <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{inr(m.amount)}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{m.count} transactions</div>
                  {m.application > 0 || m.pos > 0 ? (
                    <div className="text-[10px] text-slate-400 mt-1 flex justify-between border-t border-slate-100 pt-1">
                      <span>App: {inr(m.application)}</span>
                      <span>POS: {inr(m.pos)}</span>
                    </div>
                  ) : null}
                </Card>
              );
            })}
          </div>

          {/* Total + Bill counts */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-3 bg-emerald-50 border border-emerald-200"><Label>Total Collection</Label><Big>{inr(current.totals.total)}</Big></Card>
            <Card className="p-3"><Label>Application Bills</Label><Big className="text-slate-700">{current.totals.application_bills}</Big></Card>
            <Card className="p-3"><Label>POS Bills</Label><Big className="text-slate-700">{current.totals.pos_bills}</Big></Card>
            <Card className="p-3 bg-rose-50 border border-rose-200"><Label>Outstanding (Unpaid + Partial)</Label><Big>{inr(data.outstanding)}</Big></Card>
          </div>

          {/* Today's bills list (visible on Today tab only) */}
          {period === "today" && (
            <Card className="p-4 bg-white">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Today's Bills</div>
              {data.todays_bills.length === 0 ? (
                <div className="text-center py-6 text-slate-400 text-sm">No bills yet today.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="text-left py-2 pr-3">Bill No</th>
                        <th className="text-left py-2 pr-3">Type</th>
                        <th className="text-left py-2 pr-3">Service</th>
                        <th className="text-left py-2 pr-3">Payment</th>
                        <th className="text-right py-2 pr-3">Amount</th>
                        <th className="text-left py-2 pr-1">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.todays_bills.map((b, i) => (
                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 pr-3 font-mono text-xs text-emerald-700">{b.bill_no}</td>
                          <td className="py-2 pr-3">
                            <Badge variant="outline" className="text-[10px]">{b.type === "POS" ? <><ShoppingCart size={10} weight="fill" className="inline mr-1" />POS</> : <><Receipt size={10} weight="fill" className="inline mr-1" />App</>}</Badge>
                          </td>
                          <td className="py-2 pr-3 text-slate-700">{b.service}</td>
                          <td className="py-2 pr-3 text-slate-600">{b.payment_method}</td>
                          <td className="py-2 pr-3 text-right font-semibold">{inr(b.amount)}</td>
                          <td className="py-2 pr-1 text-[10px] text-slate-400">{new Date(b.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* Drill-down dialog */}
      <Dialog open={!!drill} onOpenChange={(v) => !v && setDrill(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{drill?.method} · {drill?.period} · {drillRows.length} transactions</DialogTitle></DialogHeader>
          {drillRows.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-400">No transactions.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
                <tr><th className="text-left py-2 pr-3">Bill</th><th className="text-left py-2 pr-3">Type</th><th className="text-left py-2 pr-3">Service</th><th className="text-right py-2 pr-3">Amount</th><th className="text-left py-2 pr-1">At</th></tr>
              </thead>
              <tbody>
                {drillRows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-mono text-xs text-emerald-700">{r.bill_no}</td>
                    <td className="py-2 pr-3"><Badge variant="outline" className="text-[10px]">{r.type}</Badge></td>
                    <td className="py-2 pr-3">{r.service}</td>
                    <td className="py-2 pr-3 text-right font-semibold">{inr(r.amount)}</td>
                    <td className="py-2 pr-1 text-[10px] text-slate-500">{new Date(r.at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-slate-300">
                <tr>
                  <td colSpan={3} className="py-2 pr-3 text-right font-semibold uppercase text-[10px] tracking-wider text-slate-500">Total</td>
                  <td className="py-2 pr-3 text-right font-bold text-lg">{inr(drillRows.reduce((s, r) => s + (r.amount || 0), 0))}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Label = ({ children }) => <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{children}</div>;
const Big = ({ children, className }) => <div className={`text-2xl font-bold text-slate-900 mt-0.5 ${className || ""}`} style={{ fontFamily: "var(--font-heading)" }}>{children}</div>;
